import type { EmailRegistrationFormData } from './email-registration-form';

import { Paragraph, SubHeading } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { AccessCodeForm } from './access-code-form';
import { EmailRegistrationForm } from './email-registration-form';
import { useDevTools } from '../../../dev-tools';
import {
  useGetWebcastAccessLazyQuery,
  useGetWebcastViewerQuery,
  useRegisterUserMutation,
} from '../../../generated/graphql-viewer';
import { formatEventStartDate } from '../../../utils';
import {
  findEventTitle,
  findViewerAccessCodePolicy,
  findViewerEmailRegistrationPolicy,
  findViewerEmailWhitelistPolicy,
} from '../../../utils/graphql-helpers';
import { ComplianceLinks } from '../../components/compliance-links';
import { ViewerLanguageSwitch } from '../../components/viewer-language-switch';
import { ViewerSpinner } from '../../components/viewer-spinner';
import { useSelectedLanguage } from '../../providers/selected-language-provider';

import styles from './access-form.module.css';

type Props = {
  onAccessCodeChange: (accessCode: string) => void;
  onSubmit: (firstName: string, lastName: string, email: string) => void;
};

export function AccessForm({ onAccessCodeChange, onSubmit }: Props) {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const { selectedLanguage } = useSelectedLanguage();
  const { mockedViewerState } = useDevTools();

  const [isCodeIncorrect, setIsCodeIncorrect] = useState(false);
  const [validationError, setValidationError] = useState<string>();

  const { data, loading, startPolling, stopPolling } = useGetWebcastViewerQuery({
    variables: { webcastId },
    skip: !webcastId,
  });

  const webcast = data?.webcast;
  const logoUrl = webcast?.appearance?.logoUrl || undefined;
  const title = findEventTitle(webcast?.contents, selectedLanguage?.code, webcast?.primaryLang.code);

  const accessCodePolicy = findViewerAccessCodePolicy(webcast?.securityPolicies);
  const emailRegistrationPolicy = findViewerEmailRegistrationPolicy(webcast?.securityPolicies);
  const emailWhitelistPolicy = findViewerEmailWhitelistPolicy(webcast?.securityPolicies);

  const emailRegistrationFormVisible = Boolean(emailRegistrationPolicy || emailWhitelistPolicy);
  const requiredRegistrationFields =
    emailRegistrationPolicy?.requiredRegistrationFields || emailWhitelistPolicy?.requiredRegistrationFields || [];
  const requireEmailVerification = Boolean(
    emailRegistrationPolicy?.requireEmailVerification || emailWhitelistPolicy?.requireEmailVerification
  );

  const [validateAccessCode] = useGetWebcastAccessLazyQuery();
  const [registerUserMutation] = useRegisterUserMutation();

  const handleAccessCodeSubmit = (accessCode: string) => {
    setIsCodeIncorrect(false);

    validateAccessCode({
      variables: { webcastId, accessCode },
      onCompleted: (data) => {
        const accessCodePolicy = findViewerAccessCodePolicy(data.webcast?.securityPolicies);

        if (accessCodePolicy?.accessGranted) {
          onAccessCodeChange(accessCode);
        } else {
          setIsCodeIncorrect(true);
        }
      },
    });
  };

  const handleEmailRegistrationSubmit = (formData: EmailRegistrationFormData) => {
    setValidationError(undefined);

    formData.email = formData.email?.toLowerCase();

    registerUserMutation({
      variables: { input: { webcastId, ...formData } },
      onCompleted: () => {
        onSubmit(formData.firstName || '', formData.lastName || '', formData.email);
      },
      onError: () => {
        if (emailRegistrationPolicy) setValidationError(t('viewer.views.accessForm.email.errors.incorrectEmail'));
        if (emailWhitelistPolicy) setValidationError(t('viewer.views.accessForm.email.errors.emailNotWhitelisted'));
      },
    });
  };

  useEffect(() => {
    startPolling(30000);
    return stopPolling;
  }, [startPolling, stopPolling]);

  if (loading) return <ViewerSpinner />;

  if (!webcast) return null;

  return (
    <main className={styles.wrapper} data-testid="access-form">
      <ViewerLanguageSwitch className={styles.languageSwitch} />

      <div className={styles.content}>
        <header className={styles.header}>
          {logoUrl && <img src={logoUrl} alt="logo" />}

          <div>
            <Paragraph data-testid="viewer-start-date">{formatEventStartDate(webcast.plannedStartAt)}</Paragraph>
            <SubHeading data-testid="viewer-title">{title}</SubHeading>
          </div>
        </header>

        {(mockedViewerState === 'access-code' || accessCodePolicy) && (
          <AccessCodeForm isCodeIncorrect={isCodeIncorrect} onSubmit={handleAccessCodeSubmit} />
        )}

        {(mockedViewerState === 'email-registration' || emailRegistrationFormVisible) && (
          <EmailRegistrationForm
            requireEmailVerification={requireEmailVerification}
            fields={requiredRegistrationFields}
            validationError={validationError}
            onSubmit={handleEmailRegistrationSubmit}
          />
        )}

        <ComplianceLinks complianceForms={webcast.complianceForms} />
      </div>
    </main>
  );
}
